// ./src/components/forms/FormVehicleEdit/index.tsx
import React, { useRef, useState, useCallback, useEffect } from 'react';

import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import {
  SubtitleSecondary,
  Paragraph,
  InputSelect,
  InputText,
  InputMask,
  ButtonMain,
} from '@components/index';
// Importações internas
import { ConfigLabel, ConfigStorage } from '@config/index';
import Vehicle from '@models/Vehicle';
import { apiRebox, newContractStorageService } from '@services/index';
import { getValidationErrors } from '@utils/errors';
import { toastify } from '@utils/notifiers';

import { schemaVehicle } from './schemaValidation';
import { ISelect, IFormVehicle } from './typing';

import {
  Container,
  FormVehicle,
  Sections,
  SectionsGroup,
  SectionsItem,
} from './styles';

interface IProps {
  vehicle: Vehicle;
}

const FormVehicleEdit: React.FC<IProps> = ({ vehicle }) => {
  const formRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [classifications, setClassifications] = useState<ISelect[]>([]);
  const [selectedClassification, setSelectedClassification] = useState<string>(
    '',
  );
  const [brands, setBrands] = useState<ISelect[]>([]);
  const [brandsTracker, setBrandsTracker] = useState<ISelect[]>([]);
  const [models, setModels] = useState<ISelect[]>([]);
  const [modelsTracker, setModelsTracker] = useState<ISelect[]>([]);

  const getClassifications = useCallback(() => {
    const classificationsList = [
      {
        label: 'Selecione o porte',
        value: '',
      },
      {
        label: 'Carros leves',
        value: 'PASSENGER_CAR',
      },
      {
        label: 'Motos',
        value: 'MOTO',
      },
      {
        label: 'Picapes ou suv',
        value: 'PICKUP_SUV',
      },
      {
        label: 'Quadricíclo',
        value: 'QUADRICYCLE',
      },
      {
        label: 'Tricíclo',
        value: 'TRICYCLE',
      },
      {
        label: 'Vans ou utilitários',
        value: 'PICKUP_VAN_UTILITIES',
      },
    ];
    setClassifications(classificationsList);
  }, []);

  const getBrands = useCallback(async (value: string, index?: number) => {
    try {
      const { data: responseCarBrands } = await apiRebox.get(`/cars`, {
        params: {
          classification: value,
          onlyBrand: true,
          per_page: 500,
        },
      });

      const brandsForTracker = responseCarBrands.data.map((brand: any) => ({
        label: brand.brand,
        value: brand.brand,
        for_tracker: brand.is_for_tracker_plan,
      }));

      const brandsAssistance = brandsForTracker.filter(
        (brand: any) => brand.for_tracker === false,
      );

      setBrands(brandsAssistance);
      setBrandsTracker(brandsForTracker);
    } catch (error) {
      toastify(
        'Ops, não conseguimos buscar as marcas de veículos existentes.',
        'error',
      );
    }
  }, []);

  const getModels = async (value: string, index?: number) => {
    try {
      const { data: responseCarModels } = await apiRebox.get(`/cars`, {
        params: {
          brand: value,
          classification: selectedClassification,
          onlyModel: true,
          per_page: 500,
        },
      });

      const modelsForTracker = responseCarModels.data.map((model: any) => ({
        label: model.model,
        value: model.model,
        for_tracker: model.is_for_tracker_plan,
      }));

      const modelsAssistance = modelsForTracker.filter(
        (brand: any) => brand.for_tracker === false,
      );

      setModels(modelsAssistance);
      setModelsTracker(modelsForTracker);
    } catch (error) {
      toastify(
        'Ops, não conseguimos buscar as modelos de veículos existentes.',
        'error',
      );
    }
  };

  const handleUpdateVehicle = async (data: IFormVehicle) => {
    try {
      setLoading(prevState => !prevState);

      const user = localStorage.getItem(
        ConfigStorage.REBOX_SESSION_LOGGED_IN_USER,
      );
      formRef.current?.setErrors({});

      await schemaVehicle.validate(data, {
        abortEarly: false,
      });

      if (user) {
        const who_requesting_change_id = JSON.parse(user).id;
        const { data: responseVehicleUpdated } = await apiRebox.put(
          `/users/vehicles/${vehicle?.id}`,
          {
            ...data,
            users_id: vehicle?.users_id,
            armored: false,
            who_requesting_change_id,
          },
        );

        const { header } = responseVehicleUpdated;

        toastify(header.message, 'success');
      }
    } catch (error: any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRef.current?.setErrors(errors);

        const {
          classification,
          brand,
          model,
          license_plate,
          year,
          color,
        } = errors;

        if (classification) toastify(classification, 'error');
        if (brand) toastify(brand, 'error');
        if (model) toastify(model, 'error');
        if (license_plate) toastify(license_plate, 'error');
        if (year) toastify(year, 'error');
        if (color) toastify(color, 'error');
      } else if (error.response) toastify(error.response.data.error, 'error');
    } finally {
      setLoading(prevState => !prevState);
    }
  };

  useEffect(() => {
    getClassifications();
    if (vehicle?.classification) getBrands(vehicle?.classification);
    if (vehicle?.brand) getModels(vehicle?.brand.toUpperCase());
  }, []);

  useEffect(() => {
    if (vehicle?.classification)
      setSelectedClassification(vehicle?.classification);
    if (vehicle?.classification) getBrands(vehicle?.classification);
    if (vehicle?.brand) getModels(vehicle?.brand.toUpperCase());
  }, [classifications]);

  return (
    <Container>
      <FormVehicle
        ref={formRef}
        onSubmit={handleUpdateVehicle}
        initialData={{
          license_plate: vehicle?.license_plate,
          year: vehicle?.year,
          imei_tracker_device_id: vehicle?.imei_tracker_device_id,
          chip_tracker_device_number: vehicle?.chip_tracker_device_number,
        }}
      >
        <Sections>
          <SectionsGroup>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Classificação
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Define o porte do veículo do cliente
              </Paragraph>
              <InputSelect
                name="classification"
                options={[
                  {
                    label: 'Selecione o porte',
                    value: '',
                  },
                  {
                    label: 'Carros leves',
                    value: 'PASSENGER_CAR',
                  },
                  {
                    label: 'Motos',
                    value: 'MOTO',
                  },
                  {
                    label: 'Picapes ou suv',
                    value: 'PICKUP_SUV',
                  },
                  {
                    label: 'Quadricíclo',
                    value: 'QUADRICYCLE',
                  },
                  {
                    label: 'Tricíclo',
                    value: 'TRICYCLE',
                  },
                  {
                    label: 'Vans ou utilitários',
                    value: 'PICKUP_VAN_UTILITIES',
                  },
                ]}
                selectedDefault={vehicle?.classification}
                onChange={event => {
                  setSelectedClassification(event.target.value);
                  getBrands(event.target.value);
                }}
                placeholder="Selecione o porte"
                tabIndex={1}
              />
            </SectionsItem>

            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Marca
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Empresa responsável pela fabricação do veículo
              </Paragraph>
              <InputSelect
                name="brand"
                options={
                  vehicle?.imei_tracker_device_id ? brandsTracker : brands
                }
                selectedDefault={
                  vehicle?.brand ? vehicle?.brand.toUpperCase() : ''
                }
                onChange={event => {
                  getModels(event.target.value);
                }}
                placeholder="Selecione a marca"
                tabIndex={2}
              />
            </SectionsItem>
          </SectionsGroup>

          <SectionsGroup>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Modelo
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Define a linha de fabricação do veículo
              </Paragraph>
              <InputSelect
                name="model"
                options={
                  vehicle?.imei_tracker_device_id ? modelsTracker : models
                }
                selectedDefault={
                  vehicle?.model ? vehicle?.model.toUpperCase() : ''
                }
                placeholder="Selecione o modelo"
                tabIndex={3}
              />
            </SectionsItem>

            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Placa
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Registro identificador do veículo no país
              </Paragraph>
              <InputMask
                name="license_plate"
                mask="aaa-9*99"
                placeholder="Digite a placa"
                style={{ textTransform: 'uppercase' }}
                tabIndex={4}
              />
            </SectionsItem>
          </SectionsGroup>

          <SectionsGroup>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Cor
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Define a cor do veículo do cliente
              </Paragraph>
              <InputSelect
                name="color"
                options={ConfigLabel.rebox.others.vehicle.color}
                selectedDefault={vehicle?.color}
                placeholder="Selecione a cor"
                tabIndex={5}
              />
            </SectionsItem>

            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Ano
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Informe o ano do modelo ou de fabricação do carro
              </Paragraph>
              <InputText
                name="year"
                type="number"
                placeholder="Digite o ano"
                min={0}
                tabIndex={6}
              />
            </SectionsItem>
          </SectionsGroup>

          <SectionsGroup>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Status
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Define a situação do veículo
              </Paragraph>
              <InputSelect
                name="status"
                options={ConfigLabel.rebox.others.vehicle.status}
                selectedDefault={
                  vehicle?.status ? vehicle?.status.toUpperCase() : ''
                }
                placeholder="Selecione a situação"
                tabIndex={7}
              />
            </SectionsItem>

            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                IMEI
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Número de IMEI do dispositivo rastreador
              </Paragraph>
              <InputText
                name="imei_tracker_device_id"
                placeholder="Insira o número do IMEI"
                tabIndex={7}
              />
            </SectionsItem>
          </SectionsGroup>

          <SectionsGroup>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Chip
              </SubtitleSecondary>
              <Paragraph
                nameColor="black"
                textAlign="start"
                opacity={0.5}
                fontSize={13}
                style={{ marginBottom: '2vh' }}
              >
                Número do chip do dispositivo rastreador
              </Paragraph>
              <InputText
                name="chip_tracker_device_number"
                placeholder="Insira o número do Chip"
                tabIndex={7}
              />
            </SectionsItem>
          </SectionsGroup>

          <ButtonMain
            type="submit"
            loading={loading}
            style={{ marginTop: '4vh', maxWidth: 250 }}
            tabIndex={8}
          >
            Salvar
          </ButtonMain>
        </Sections>
      </FormVehicle>
    </Container>
  );
};

export default FormVehicleEdit;
